import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import * as JsxRuntime from 'react/jsx-runtime';
import { useSyncExternalStore, type ComponentType } from 'react';
import { api } from '../../api/client';
import * as sdk from '../sdk/react';
import * as place from '../kit/place';
import * as phrases from '../phrases';
import * as sceneView from '../scenes/SceneView';
import * as scenes from '../scenes/scenes';
import { registerKind, type KindDef } from '../../concepts/kinds';
import { TOOLKIT_VERSION, type GameModule } from '../sdk/toolkit';

export type AddonEntry = { id: string; kind: string; version: string; toolkit: number; script: string; style: string | null };

export type AddonEditorProps = { config: Record<string, unknown>; onChange: (next: Record<string, unknown>) => void };

export type AddonDefinition = {
  kind: string;
  version: string;
  toolkit: number;
  module: GameModule;
  kindDef?: KindDef;
  editor?: ComponentType<AddonEditorProps>;
};

const LIST_MAX_AGE_MS = 60_000;
const LOAD_TIMEOUT_MS = 15_000;

const defined = new Map<string, AddonDefinition>();
const waiting = new Map<string, (d: AddonDefinition) => void>();
const loading = new Map<string, Promise<AddonDefinition | null>>();
let entries: AddonEntry[] = [];
let listedAt = 0;
let listing: Promise<AddonEntry[]> | null = null;
let ready = false;
let version = 0;
const listeners = new Set<() => void>();

const key = (kind: string, v: string) => `${kind}@${v}`;
const changed = () => { version++; listeners.forEach((l) => l()); };

declare global {
  interface Window { __EL_HOST__?: unknown }
}

function installBridge() {
  if (window.__EL_HOST__) return;
  window.__EL_HOST__ = Object.freeze({
    toolkit: TOOLKIT_VERSION,
    React,
    ReactDOMClient,
    JsxRuntime,
    sdk,
    blocks: Object.freeze({ place, phrases, sceneView, scenes }),
    define(def: AddonDefinition) {
      if (!def || typeof def.kind !== 'string' || typeof def.version !== 'string' || !def.module) return;
      if (def.toolkit !== TOOLKIT_VERSION || def.module.toolkit !== TOOLKIT_VERSION) return;
      const el = document.currentScript as HTMLScriptElement | null;
      const expected = el?.dataset.addonKind;
      if (!expected || expected !== def.kind) return;
      const k = el.dataset.addonKey as string;
      defined.set(k, def);
      waiting.get(k)?.(def);
      waiting.delete(k);
    },
  });
}

function addStyle(url: string) {
  if (document.querySelector(`link[data-addon-style="${CSS.escape(url)}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.dataset.addonStyle = url;
  document.head.appendChild(link);
}

function load(entry: AddonEntry): Promise<AddonDefinition | null> {
  const k = key(entry.kind, entry.version);
  const done = defined.get(k);
  if (done) return Promise.resolve(done);
  const pending = loading.get(k);
  if (pending) return pending;
  installBridge();
  if (entry.style) addStyle(entry.style);
  const p = new Promise<AddonDefinition | null>((resolve) => {
    const timer = window.setTimeout(() => { waiting.delete(k); resolve(null); }, LOAD_TIMEOUT_MS);
    waiting.set(k, (d) => { window.clearTimeout(timer); resolve(d); });
    const script = document.createElement('script');
    script.src = entry.script;
    script.dataset.addonKey = k;
    script.dataset.addonKind = entry.kind;
    script.async = true;
    script.onerror = () => { window.clearTimeout(timer); waiting.delete(k); resolve(null); };
    document.head.appendChild(script);
  }).then((d) => {
    loading.delete(k);
    if (d?.kindDef) registerKind(d.kindDef);
    changed();
    return d;
  });
  loading.set(k, p);
  return p;
}

async function list(force = false): Promise<AddonEntry[]> {
  if (!force && listing && Date.now() - listedAt < LIST_MAX_AGE_MS) return listing;
  listedAt = Date.now();
  listing = api<AddonEntry[]>('/addons', { token: true })
    .then((rows) => { entries = rows.filter((r) => r.toolkit === TOOLKIT_VERSION); return entries; })
    .catch(() => entries);
  return listing;
}

export async function loadAddons(force = false): Promise<void> {
  const rows = await list(force);
  await Promise.all(rows.map(load));
  if (!ready) { ready = true; changed(); }
}

export async function addonFor(kind: string): Promise<AddonDefinition | null> {
  const rows = await list();
  const entry = rows.find((r) => r.kind === kind);
  return entry ? load(entry) : null;
}

export function addonEditor(kind: string): ComponentType<AddonEditorProps> | null {
  const entry = entries.find((r) => r.kind === kind);
  return entry ? defined.get(key(entry.kind, entry.version))?.editor ?? null : null;
}

export function useAddons(): { ready: boolean; version: number } {
  const v = useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => version,
  );
  return { ready, version: v };
}
